import { useState } from "react";
import { toast } from "sonner";
import { useSite } from "../../context/SiteContext";
import { Reveal, Overline } from "../Reveal";
import { api, fmtErr } from "../../lib/api";

const inputCls = "w-full border border-white/25 bg-transparent px-4 py-3.5 text-sm text-white placeholder-white/35 outline-none transition-colors focus:border-[#D60024]";

export default function Contact() {
    const { site, tx } = useSite();
    const s = site?.settings || {};
    const c = site?.content || {};
    const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", topic: "", message: "" });
    const [sending, setSending] = useState(false);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await api.post("/contact", form);
            toast.success(tx({ en: "Message sent. We'll be in touch soon.", pt: "Mensagem enviada. Entraremos em contacto em breve." }));
            setForm({ name: "", company: "", email: "", phone: "", topic: "", message: "" });
        } catch (err) {
            toast.error(fmtErr(err.response?.data?.detail));
        } finally {
            setSending(false);
        }
    };

    const rows = [
        { label: "Email", value: s.contact_email, href: s.contact_email ? `mailto:${s.contact_email}` : null, testId: "contact-email" },
        { label: tx({ en: "Phone", pt: "Telefone" }), value: s.phone, href: s.phone ? `tel:${s.phone.replace(/\s/g, "")}` : null, testId: "contact-phone" },
        { label: "WhatsApp", value: s.whatsapp, href: s.whatsapp ? `https://wa.me/${s.whatsapp.replace(/[^\d]/g, "")}` : null, testId: "contact-whatsapp" },
        { label: "Instagram", value: s.instagram?.replace(/https?:\/\/(www\.)?instagram\.com\//, "@"), href: s.instagram, testId: "contact-instagram" },
        { label: "LinkedIn", value: "LinkedIn", href: s.linkedin, testId: "contact-linkedin" },
        { label: tx({ en: "Office", pt: "Escritório" }), value: tx(s.location), href: null, testId: "contact-location" },
    ];

    return (
        <section id="contact" data-testid="contact-section" className="grain relative bg-[#0A0A0A] px-6 py-32 text-white md:px-12 md:py-48 lg:px-24">
            <div className="grid gap-20 lg:grid-cols-2">
                <div>
                    <Overline testId="contact-overline">Contact — 08</Overline>
                    <Reveal>
                        <h2 className="font-display mt-6 text-[clamp(3rem,7.5vw,7rem)] leading-[0.85]" data-testid="contact-title">
                            {tx(c.contact_title1) || "START A"}
                            <br />
                            <span className="text-[#D60024]">{tx(c.contact_title2) || "CONVERSATION."}</span>
                        </h2>
                    </Reveal>
                    <div className="mt-14 space-y-0">
                        {rows.filter((r) => r.value).map((r) => (
                            <div key={r.label} className="flex items-baseline justify-between gap-6 border-t border-white/12 py-4 last:border-b">
                                <span className="text-[11px] font-semibold uppercase tracking-[0.25em] text-white/45">{r.label}</span>
                                {r.href ? (
                                    <a href={r.href} target={r.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" data-testid={r.testId} className="u-line text-sm text-white/85 md:text-base">
                                        {r.value}
                                    </a>
                                ) : (
                                    <span data-testid={r.testId} className="text-sm text-white/85 md:text-base">{r.value}</span>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                <Reveal delay={0.1}>
                    <form onSubmit={submit} className="space-y-5 lg:pt-24" data-testid="contact-form">
                        <div className="grid gap-5 sm:grid-cols-2">
                            <input required data-testid="contact-name-input" placeholder={tx({ en: "Name *", pt: "Nome *" })} value={form.name} onChange={set("name")} className={inputCls} />
                            <input data-testid="contact-company-input" placeholder={tx({ en: "Company", pt: "Empresa" })} value={form.company} onChange={set("company")} className={inputCls} />
                        </div>
                        <div className="grid gap-5 sm:grid-cols-2">
                            <input required type="email" data-testid="contact-email-input" placeholder="Email *" value={form.email} onChange={set("email")} className={inputCls} />
                            <input data-testid="contact-phone-input" placeholder={tx({ en: "Phone (optional)", pt: "Telefone (opcional)" })} value={form.phone} onChange={set("phone")} className={inputCls} />
                        </div>
                        <input data-testid="contact-topic-input" placeholder={tx({ en: "What would you like to discuss?", pt: "O que gostaria de discutir?" })} value={form.topic} onChange={set("topic")} className={inputCls} />
                        <textarea required rows={5} data-testid="contact-message-input" placeholder={tx({ en: "Message *", pt: "Mensagem *" })} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} />
                        <button
                            type="submit"
                            disabled={sending}
                            data-testid="contact-submit-btn"
                            className="group inline-flex items-center gap-3 border border-[#D60024] bg-[#D60024] px-8 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:bg-transparent hover:text-[#D60024] disabled:opacity-50"
                        >
                            {sending ? "..." : tx(s.contact_cta) || "START A CONVERSATION"}
                            <span className="inline-block font-display text-xl leading-none transition-transform duration-300 group-hover:rotate-45" aria-hidden="true">+</span>
                        </button>
                    </form>
                </Reveal>
            </div>
        </section>
    );
}
